import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useGetWebsitesQuery, useUpdateSubscriptionPlanMutation } from "../../app/api/platform/platformWebsitesApiSlice";
import { useGetWebsitesAnalyticsScansQuery } from "../../app/api/platform/platformApiSlice";
import {
  useGetCustomerOverviewQuery,
  useGetNpsHistoryQuery,
  useCreateNpsMutation,
  useUpdateSubscriptionMetaMutation,
} from "../../app/api/console/consoleCustomerAnalyticsApiSlice";
import CustomerBrandActivityTrends from "../../components/BI/CustomerBrandActivityTrends";
import CustomerBrandActiveUsers from "../../components/BI/CustomerBrandActiveUsers";
import { PAYING_TIERS, ALL_PLAN_TIERS, PLAN_DISPLAY, PLAN_STYLES, displayPlan } from "../../constants/customers";
import CustomerSessions from "../../components/BI/CustomerSessions";
import { platformFrontBase } from "./ControlTower/hubScanLinks";
import { agentsBreakdown, buildAddCustomerResults, findingsBreakdown, mostRecentConsoleSignal } from "./customerAnalyticsHelpers";

function startOfLocalDay(d) {
  const x = new Date(d);
  return new Date(x.getFullYear(), x.getMonth(), x.getDate());
}

/** Whole calendar days since the local date (0 = same calendar day as today). */
function calendarDaysSince(date) {
  const t = startOfLocalDay(date);
  const today = startOfLocalDay(new Date());
  return Math.round((today - t) / (1000 * 60 * 60 * 24));
}

function fmtHealthPts(n) {
  return (Math.round(n * 10) / 10).toFixed(1);
}

/** Single portal node — panel only (position passed in). */
function HealthBreakdownTooltipPanel({ detail, score, anchorRect }) {
  if (!detail?.parts || !anchorRect) return null;
  const pad = 8;
  const maxW = Math.min(360, typeof window !== "undefined" ? window.innerWidth - 2 * pad : 360);
  const leftClamped = Math.max(
    pad,
    Math.min(anchorRect.left, (typeof window !== "undefined" ? window.innerWidth : anchorRect.left) - pad - maxW)
  );
  return (
    <div
      role='tooltip'
      style={{
        position: "fixed",
        left: leftClamped,
        top: anchorRect.top - 10,
        transform: "translateY(-100%)",
        zIndex: 2147483647,
        width: "max-content",
        maxWidth: "min(360px, calc(100vw - 16px))",
      }}
      className='pointer-events-none rounded-xl border border-border-default bg-white shadow-2xl ring-1 ring-black/5'>
      <div className='border-b border-border-subtle bg-surface-neutral/60 px-3 py-2'>
        <div className='flex items-baseline justify-between gap-4'>
          <span className='text-[11px] font-semibold uppercase tracking-[0.08em] text-text-secondary'>
            Health breakdown
          </span>
          <span className='font-mono text-sm font-bold tabular-nums text-text-primary'>{score}</span>
        </div>
      </div>
      <div className='px-3 py-2'>
        <dl className='space-y-2.5'>
          {detail.parts.map((p) => (
            <div key={p.key} className='border-b border-border-subtle/80 pb-2.5 last:border-0 last:pb-0'>
              <div className='flex items-start justify-between gap-6'>
                <dt className='min-w-0 flex-1 text-left'>
                  <span className='text-[13px] font-medium leading-tight text-text-primary'>{p.label}</span>
                  <span className='ml-1.5 text-[11px] font-medium text-text-tertiary'>{p.weightPct}%</span>
                </dt>
                <dd className='shrink-0 font-mono text-[13px] font-semibold tabular-nums text-text-primary'>
                  {fmtHealthPts(p.points)}
                  <span className='ml-0.5 text-[11px] font-normal text-text-tertiary'>pts</span>
                </dd>
              </div>
              <div className='mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[11px] leading-snug text-text-tertiary'>
                <span className='text-text-tertiary/90'>Subscore</span>
                <span className='font-mono tabular-nums text-text-secondary'>{p.raw}/100</span>
                <span className='col-span-2 text-text-tertiary'>{p.note}</span>
              </div>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

/** Generic hover card (agents / findings breakdown) sharing the health tooltip's portal + placement. */
function HoverCardPanel({ title, headline, rows, anchorRect }) {
  if (!anchorRect) return null;
  const pad = 8;
  const maxW = Math.min(360, typeof window !== "undefined" ? window.innerWidth - 2 * pad : 360);
  const leftClamped = Math.max(
    pad,
    Math.min(anchorRect.left, (typeof window !== "undefined" ? window.innerWidth : anchorRect.left) - pad - maxW)
  );
  return (
    <div
      role='tooltip'
      style={{
        position: "fixed",
        left: leftClamped,
        top: anchorRect.top - 10,
        transform: "translateY(-100%)",
        zIndex: 2147483647,
        width: "max-content",
        maxWidth: "min(360px, calc(100vw - 16px))",
      }}
      className='pointer-events-none rounded-xl border border-border-default bg-white shadow-2xl ring-1 ring-black/5'>
      <div className='border-b border-border-subtle bg-surface-neutral/60 px-3 py-2'>
        <span className='text-[11px] font-semibold uppercase tracking-[0.08em] text-text-secondary'>{title}</span>
        {headline ? <p className='text-[13px] font-medium text-text-primary mt-0.5'>{headline}</p> : null}
      </div>
      <div className='px-3 py-2'>
        <dl className='space-y-1'>
          {rows.map((r, i) => (
            <div key={`${r.label}-${i}`} className={`flex items-baseline justify-between gap-6 ${r.sub ? "pl-2" : ""}`}>
              <dt className={`min-w-0 text-left text-[12px] ${r.sub ? "text-text-tertiary" : "text-text-primary"} truncate`}>
                {r.href ? <span className='underline decoration-dotted underline-offset-2'>{r.label} ↗</span> : r.label}
              </dt>
              <dd className='shrink-0 font-mono text-[12px] tabular-nums text-text-secondary'>{r.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

const NPS_EMOJIS = [
  { score: 1, emoji: "\uD83D\uDE21", label: "Very Dissatisfied" },
  { score: 2, emoji: "\uD83D\uDE1E", label: "Dissatisfied" },
  { score: 3, emoji: "\uD83D\uDE10", label: "Neutral" },
  { score: 4, emoji: "\uD83D\uDE0A", label: "Satisfied" },
  { score: 5, emoji: "\uD83D\uDE0D", label: "Very Satisfied" },
];

function NpsEmojiPicker({ currentScore, onSelect }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const current = NPS_EMOJIS.find((e) => e.score === currentScore);

  return (
    <div className='relative' ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className='flex items-center gap-1 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors'
        title={current ? current.label : "Set NPS"}>
        <span className='text-xl'>{current ? current.emoji : "\u2796"}</span>
        {current && <span className='text-xs text-gray-500'>{current.score}/5</span>}
      </button>
      {isOpen && (
        <div className='absolute z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 flex gap-1'>
          {NPS_EMOJIS.map((item) => (
            <button
              key={item.score}
              onClick={() => {
                onSelect(item.score);
                setIsOpen(false);
              }}
              className={`flex flex-col items-center p-2 rounded-md hover:bg-gray-100 transition-colors ${
                currentScore === item.score ? "bg-blue-50 ring-2 ring-blue-300" : ""
              }`}
              title={item.label}>
              <span className='text-2xl'>{item.emoji}</span>
              <span className='text-[10px] text-gray-500 mt-0.5'>{item.score}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NpsHistoryChart({ websiteId }) {
  const { data, isLoading } = useGetNpsHistoryQuery({ websiteId }, { skip: !websiteId });

  const chartData = useMemo(() => {
    if (!data?.entries) return [];
    return [...data.entries]
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      .map((entry) => ({
        date: new Date(entry.created_at).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        score: entry.score,
        fullDate: new Date(entry.created_at).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
      }));
  }, [data]);

  if (isLoading) {
    return <div className='flex items-center justify-center h-48 text-gray-400'>Loading history...</div>;
  }

  if (chartData.length === 0) {
    return (
      <div className='flex items-center justify-center h-48 text-gray-400'>
        No NPS history yet. Set a score to start tracking.
      </div>
    );
  }

  return (
    <div className='h-48'>
      <ResponsiveContainer width='100%' height='100%'>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray='3 3' />
          <XAxis dataKey='date' tick={{ fontSize: 11 }} />
          <YAxis domain={[0, 6]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 11 }} />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload;
              const nps = NPS_EMOJIS.find((e) => e.score === d.score);
              return (
                <div className='bg-white border border-gray-200 rounded-lg shadow-sm p-3 text-sm'>
                  <p className='font-medium'>{d.fullDate}</p>
                  <p className='mt-1'>
                    {nps?.emoji} {nps?.label} ({d.score}/5)
                  </p>
                </div>
              );
            }}
          />
          <Line
            type='monotone'
            dataKey='score'
            stroke='#6366f1'
            strokeWidth={2}
            dot={{ fill: "#6366f1", r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function AgentsPerWeekChart({ websiteId, enabled = true }) {
  const { data: analyticsData = [] } = useGetWebsitesAnalyticsScansQuery(
    { websiteIds: [websiteId], useProd: true },
    { skip: !enabled || !websiteId }
  );

  const scans = useMemo(() => {
    const row = analyticsData.find((r) => r.website_id === websiteId);
    if (!row) return [];
    return (row.scans || [])
      .map((scan) => {
        const raw = scan.start_time || scan.date_added || scan.created_at;
        if (!raw) return null;
        const ts = new Date(raw);
        if (Number.isNaN(ts.getTime())) return null;
        return {
          date: ts.toISOString(),
          title: scan.title || scan.scan_template_id || "Untitled",
        };
      })
      .filter(Boolean);
  }, [analyticsData, websiteId]);

  const chartData = useMemo(() => {
    if (!scans || scans.length === 0) return [];

    const now = new Date();
    const weeks = [];
    for (let i = 11; i >= 0; i--) {
      const weekEnd = new Date(now);
      weekEnd.setDate(weekEnd.getDate() - i * 7);
      const weekStart = new Date(weekEnd);
      weekStart.setDate(weekStart.getDate() - 6);
      weeks.push({ start: weekStart, end: weekEnd });
    }

    const fmt = (d) =>
      d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return weeks.map(({ start, end }) => {
      const matched = scans.filter((s) => {
        if (!s.date) return false;
        const d = new Date(s.date);
        return d >= start && d <= end;
      });
      // Range label — axis used to show only week start, which reads like "last run on Mar 14"
      const week = `${fmt(start)}–${fmt(end)}`;
      return { week, agents: matched.length, titles: matched.map((s) => s.title) };
    });
  }, [scans]);

  if (chartData.length === 0) {
    return <div className='flex items-center justify-center h-48 text-gray-400'>No agent launch data available.</div>;
  }

  return (
    <div className='h-48'>
      <ResponsiveContainer width='100%' height='100%'>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray='3 3' />
          <XAxis dataKey='week' tick={{ fontSize: 10 }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload;
              const titleCounts = {};
              d.titles.forEach((t) => {
                titleCounts[t] = (titleCounts[t] || 0) + 1;
              });
              const entries = Object.entries(titleCounts).sort((a, b) => b[1] - a[1]);
              return (
                <div className='bg-white border border-gray-200 rounded-lg shadow-sm p-3 text-sm max-w-xs'>
                  <p className='font-medium'>{d.week}</p>
                  <p className='text-green-700 mb-1'>
                    {d.agents} agent{d.agents !== 1 ? "s" : ""} launched
                  </p>
                  {entries.length > 0 && (
                    <ul className='mt-1 space-y-0.5 max-h-40 overflow-y-auto'>
                      {entries.map(([title, count]) => (
                        <li key={title} className='text-xs text-gray-600 truncate'>
                          <span className='font-medium text-gray-800'>{count}x</span> {title}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            }}
          />
          <Bar dataKey='agents' fill='#22c55e' radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function PlanSelector({ currentPlan, onPlanChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className='relative' ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`px-2.5 py-1 text-caption font-semibold rounded-lg cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-accent-purple/30 transition-all ${
          PLAN_STYLES[displayPlan(currentPlan)] || "bg-surface-neutral-subtle text-text-secondary"
        }`}>
        {PLAN_DISPLAY[displayPlan(currentPlan)] || displayPlan(currentPlan)}
      </button>
      {isOpen && (
        <div className='absolute z-50 mt-2 bg-white border border-border-subtle rounded-xl shadow-lg py-1 min-w-[140px] animate-fade-in'>
          {ALL_PLAN_TIERS.map((tier) => (
            <button
              key={tier}
              onClick={() => {
                if (tier !== currentPlan) onPlanChange(tier);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-1.5 text-body-xs hover:bg-surface-neutral transition-colors flex items-center gap-2 ${
                tier === currentPlan ? "font-bold bg-surface-neutral" : ""
              }`}>
              <span className={`w-2 h-2 rounded-full inline-block ${PLAN_STYLES[tier]?.split(" ")[0] || "bg-surface-neutral-subtle"}`} />
              {PLAN_DISPLAY[tier]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CustomerAnalytics() {
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [sortField, setSortField] = useState("health");
  const [sortDir, setSortDir] = useState("asc");
  const [tierFilters, setTierFilters] = useState(new Set(PAYING_TIERS));
  const [starredOnly, setStarredOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [addSearchTerm, setAddSearchTerm] = useState("");
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const addSearchRef = useRef(null);

  /** Single hover card (one portal) for health / agents / findings — per-row portals
   *  stacked when leave fired out of order. */
  const [hoverCard, setHoverCard] = useState(null); // { kind, prodId }
  const [hoverCardRect, setHoverCardRect] = useState(null);
  const hoverCardAnchorRef = useRef(null);

  const openHoverCard = useCallback((kind, prodId, el) => {
    hoverCardAnchorRef.current = el;
    setHoverCard({ kind, prodId });
    setHoverCardRect(el.getBoundingClientRect());
  }, []);

  const closeHoverCardIf = useCallback((kind, prodId) => {
    setHoverCard((prev) => {
      if (prev && prev.kind === kind && prev.prodId === prodId) {
        hoverCardAnchorRef.current = null;
        return null;
      }
      return prev;
    });
  }, []);

  useEffect(() => {
    if (!hoverCard) {
      setHoverCardRect(null);
      return undefined;
    }
    const el = hoverCardAnchorRef.current;
    if (!el) return undefined;
    const sync = () => setHoverCardRect(el.getBoundingClientRect());
    window.addEventListener("scroll", sync, true);
    window.addEventListener("resize", sync);
    return () => {
      window.removeEventListener("scroll", sync, true);
      window.removeEventListener("resize", sync);
    };
  }, [hoverCard]);

  const hubBase = platformFrontBase("prod");

  const [updateSubscriptionPlan] = useUpdateSubscriptionPlanMutation();
  const [updateSubscriptionMeta] = useUpdateSubscriptionMetaMutation();
  const [createNps] = useCreateNpsMutation();

  // Close add-customer dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (addSearchRef.current && !addSearchRef.current.contains(e.target)) setShowAddDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Single aggregated call replaces 5 prior endpoints. Backend computes health + percentiles.
  const {
    data: overviewData = [],
    isLoading: overviewLoading,
    refetch: refetchOverview,
  } = useGetCustomerOverviewQuery();

  // Websites list is only used for the Add Customer search (every website, incl.
  // ones already tracked — those show their current tier instead of "Set tier").
  const { data: websitesData = [] } = useGetWebsitesQuery({ useProd: true });

  const biActivityWindow = useMemo(() => {
    const end = new Date();
    const start = new Date(Date.now() - 5 * 30 * 24 * 60 * 60 * 1000);
    return {
      startDate: start.toISOString().split("T")[0],
      endDate: end.toISOString().split("T")[0],
    };
  }, []);

  // Normalize overview rows into the camelCase shape the rest of the component uses.
  const customers = useMemo(() => {
    return overviewData.map((row) => {
      const lastLogin = row.bi_activity?.last_login_at ? new Date(row.bi_activity.last_login_at) : null;
      const lastAct = row.bi_activity?.last_activity_at ? new Date(row.bi_activity.last_activity_at) : null;
      return {
        prodId: row.website_id,
        name: row.name,
        plan: row.plan,
        starred: !!row.starred,
        nps: row.nps || null,
        agents30d: row.agents_30d || 0,
        agents: (row.agents || []).map((a) => ({ ...a, last_run_at: a.last_run_at ? new Date(a.last_run_at) : null })),
        agentsAllTime: row.agents_all_time || 0,
        runs30d: row.runs_30d || 0,
        findings: row.findings
          ? { ...row.findings, last_interaction_at: row.findings.last_interaction_at ? new Date(row.findings.last_interaction_at) : null }
          : null,
        activeDays30d: row.active_days_30d || 0,
        activeUsers30d: row.active_users_30d || 0,
        lastActiveDate: row.last_agent_launch_at ? new Date(row.last_agent_launch_at) : null,
        biActivity: row.bi_activity
          ? {
              actions_per_week: row.bi_activity.actions_per_week,
              rate_days: row.bi_activity.rate_days ?? null,
              total_actions: row.bi_activity.total_actions,
              login_count: row.bi_activity.login_count,
              last_login_at: lastLogin,
              last_activity_at: lastAct,
            }
          : null,
        health: row.health
          ? {
              score: row.health.score,
              parts: (row.health.parts || []).map((p) => ({
                key: p.key,
                label: p.label,
                weightPct: p.weight_pct,
                raw: p.raw,
                points: p.points,
                note: p.note,
              })),
            }
          : { score: 0, parts: [] },
      };
    });
  }, [overviewData]);

  const customerByProdId = useMemo(() => {
    const map = {};
    customers.forEach((c) => { map[c.prodId] = c; });
    return map;
  }, [customers]);

  const handleNpsChange = useCallback(
    async (websiteId, score) => {
      const existing = customerByProdId[websiteId]?.nps;
      await createNps({
        website_id: websiteId,
        score,
        next_steps: existing?.next_steps || null,
      });
    },
    [createNps, customerByProdId]
  );

  const handleStarToggle = useCallback(
    async (websiteId, nextStarred) => {
      await updateSubscriptionMeta({ websiteId, starred: nextStarred });
    },
    [updateSubscriptionMeta]
  );

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortedCustomers = useMemo(() => {
    const planOrder = { enterprise: 0, growth: 1, essentials: 2, demo: 3, free: 4 };
    return [...customers].sort((a, b) => {
      let cmp = 0;
      if (sortField === "name") {
        cmp = a.name.localeCompare(b.name);
      } else if (sortField === "plan") {
        cmp = (planOrder[a.plan] ?? 99) - (planOrder[b.plan] ?? 99);
      } else if (sortField === "nps") {
        cmp = (a.nps?.score || 0) - (b.nps?.score || 0);
      } else if (sortField === "agents") {
        cmp = a.agents30d - b.agents30d || a.runs30d - b.runs30d;
      } else if (sortField === "findings") {
        cmp = (a.findings?.interacted || 0) - (b.findings?.interacted || 0);
      } else if (sortField === "activity") {
        cmp = (a.biActivity?.actions_per_week || 0) - (b.biActivity?.actions_per_week || 0);
      } else if (sortField === "health") {
        cmp = (a.health?.score || 0) - (b.health?.score || 0);
      } else if (sortField === "lastActive") {
        const da = a.lastActiveDate || new Date(0);
        const db = b.lastActiveDate || new Date(0);
        cmp = da - db;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [customers, sortField, sortDir]);

  const handlePlanChange = useCallback(
    async (websiteId, newPlan) => {
      // The mutation lives in platformApiSlice and only invalidates "Websites";
      // the customer rows come from the CustomerOverview query in a different
      // API slice, so refetch it explicitly to reflect the new tier.
      try {
        await updateSubscriptionPlan({ websiteId, subscriptionPlan: newPlan }).unwrap();
      } finally {
        refetchOverview();
      }
    },
    [updateSubscriptionPlan, refetchOverview]
  );

  const handleAddCustomer = useCallback(
    async (websiteId, plan) => {
      try {
        await updateSubscriptionPlan({ websiteId, subscriptionPlan: plan }).unwrap();
      } finally {
        refetchOverview();
      }
      setAddSearchTerm("");
      setShowAddDropdown(false);
    },
    [updateSubscriptionPlan, refetchOverview]
  );

  // Filter by tier chips + starred + search
  const filteredCustomers = useMemo(() => {
    let result = sortedCustomers;
    if (starredOnly) {
      result = result.filter((c) => c.starred);
    }
    if (tierFilters.size > 0 && tierFilters.size < ALL_PLAN_TIERS.length) {
      result = result.filter((c) => tierFilters.has(displayPlan(c.plan)));
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (c) => c.name.toLowerCase().includes(term) || c.prodId.toLowerCase().includes(term)
      );
    }
    return result;
  }, [sortedCustomers, tierFilters, starredOnly, searchTerm]);

  const addSearchResults = useMemo(
    () => buildAddCustomerResults(websitesData, customerByProdId, addSearchTerm),
    [websitesData, customerByProdId, addSearchTerm]
  );

  const activityHeaderTitle =
    "Same interactive-activity rollup as the BI Dashboard (email sends never count). Trailing rate: events in the last 90 days — or since the customer's first activity, if more recent — per week. Hover a value for totals over the last year.";

  const SortHeader = ({ field, children, title: headerTitle }) => (
    <th
      title={headerTitle}
      onClick={() => toggleSort(field)}
      className='px-4 py-2.5 text-left text-caption font-semibold text-text-secondary uppercase tracking-[0.06em] cursor-pointer hover:text-text-primary select-none transition-colors'>
      <span className='flex items-center gap-1'>
        {children}
        {sortField === field && (
          <svg
            className={`h-3 w-3 transition-transform ${sortDir === "desc" ? "rotate-180" : ""}`}
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 15l7-7 7 7' />
          </svg>
        )}
      </span>
    </th>
  );

  const stats = useMemo(() => {
    const tierCounts = {};
    customers.forEach((c) => {
      const label = PLAN_DISPLAY[displayPlan(c.plan)] || displayPlan(c.plan);
      tierCounts[label] = (tierCounts[label] || 0) + 1;
    });
    const npsScores = customers.map((c) => c.nps?.score).filter((s) => typeof s === "number");
    const avgNps =
      npsScores.length > 0
        ? (npsScores.reduce((s, n) => s + n, 0) / npsScores.length).toFixed(1)
        : "N/A";
    return { total: customers.length, tierCounts, avgNps };
  }, [customers]);

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-h2 text-text-primary'>Customer Analytics</h1>
        <p className='text-body-sm text-text-secondary mt-1'>Track customer satisfaction, plan tiers, and usage metrics.</p>
      </div>

      {/* Summary Cards */}
      <div className='flex flex-wrap gap-3'>
        <div className='bg-white rounded-2xl border border-border-subtle p-5 min-w-[130px]'>
          <p className='text-body-xs font-medium text-text-secondary mb-1'>Total Customers</p>
          <p className='text-metric text-text-primary'>{stats.total}</p>
        </div>
        {Object.entries(stats.tierCounts).map(([label, count]) => (
          <div key={label} className='bg-white rounded-2xl border border-border-subtle p-5 min-w-[130px]'>
            <p className='text-body-xs font-medium text-text-secondary mb-1'>{label}</p>
            <p className='text-metric text-text-primary'>{count}</p>
          </div>
        ))}
        <div className='bg-white rounded-2xl border border-border-subtle p-5 min-w-[130px]'>
          <p className='text-body-xs font-medium text-text-secondary mb-1'>Avg NPS</p>
          <p className='text-metric text-accent-purple'>{stats.avgNps}</p>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className='flex flex-wrap items-center gap-3'>
        <div className='flex items-center gap-2'>
          <span className='text-caption font-semibold text-text-tertiary uppercase tracking-wide'>Tiers:</span>
          
          <button
            onClick={() => setTierFilters(new Set(PAYING_TIERS))}
            className={`px-2.5 py-1 text-body-xs font-medium rounded-lg transition-colors ${
              PAYING_TIERS.every((t) => tierFilters.has(t)) && tierFilters.size === PAYING_TIERS.length
                ? "bg-accent-purple/10 text-accent-purple"
                : "bg-surface-neutral text-text-tertiary hover:bg-surface-neutral-subtle"
            }`}>
            All Paying
          </button>

            {PAYING_TIERS.map((tier) => {
              const isAllPaying =
                PAYING_TIERS.every((t) => tierFilters.has(t)) &&
                tierFilters.size === PAYING_TIERS.length;
              const isSelected = tierFilters.has(tier) && !isAllPaying;

              return (
                <button
                  key={tier}
                  onClick={() => {
                    setTierFilters((prev) => {
                      const isCurrentlyAllPaying =
                        PAYING_TIERS.every((t) => prev.has(t)) &&
                        prev.size === PAYING_TIERS.length;
                      if (isCurrentlyAllPaying) {
                        return new Set([tier]);
                      }
                      const next = new Set(prev);
                      if (next.has(tier)) {
                        if (next.size > 1) next.delete(tier);
                        else return new Set(PAYING_TIERS);
                      } else {
                        next.add(tier);
                      }
                      return next;
                    });
                  }}
                  className={`px-2.5 py-1 text-body-xs font-medium rounded-lg transition-colors ${
                    isSelected
                      ? PLAN_STYLES[tier]
                      : "bg-surface-neutral text-text-tertiary hover:bg-surface-neutral-subtle"
                  }`}>
                  {PLAN_DISPLAY[tier]}
                </button>
              );
            })}

            <button
              onClick={() => {
                setTierFilters((prev) => {
                  const next = new Set(prev);
                  if (next.has("demo") && next.size === 1) {
                    return new Set(PAYING_TIERS);
                  }
                  if (next.has("demo")) next.delete("demo");
                  else next.add("demo");
                  return next;
                });
              }}
              className={`px-2.5 py-1 text-body-xs font-medium rounded-lg transition-colors ${
                tierFilters.has("demo")
                  ? PLAN_STYLES.demo
                  : "bg-surface-neutral text-text-tertiary hover:bg-surface-neutral-subtle"
              }`}>
              Demo
            </button>

            <div className='h-5 w-px bg-border-subtle mx-1' />

            <button
              onClick={() => setStarredOnly((v) => !v)}
              title='Show only starred customers (most important — churn risk is not acceptable)'
              className={`px-2.5 py-1 text-body-xs font-medium rounded-lg transition-colors inline-flex items-center gap-1.5 ${
                starredOnly
                  ? "bg-amber-100 text-amber-700"
                  : "bg-surface-neutral text-text-tertiary hover:bg-surface-neutral-subtle"
              }`}>
              <svg
                className={`h-3.5 w-3.5 ${starredOnly ? "fill-amber-500" : "fill-none"}`}
                viewBox='0 0 24 24'
                stroke='currentColor'
                strokeWidth={2}>
                <path strokeLinecap='round' strokeLinejoin='round' d='M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118L2.098 10.1c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.518-4.674z' />
              </svg>
              Starred
            </button>
          </div>

          <div className='h-5 w-px bg-border-subtle' />

          <div className='relative flex-1 min-w-[200px] max-w-xs'>
            <svg className='absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-tertiary pointer-events-none' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}><path strokeLinecap='round' strokeLinejoin='round' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' /></svg>
            <input
              type='text'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder='Search customers...'
              className='w-full pl-9 pr-8 py-2 text-body-xs bg-white border border-border-default rounded-xl focus:outline-none focus:ring-2 focus:ring-accent-purple/20 focus:border-accent-purple/40 placeholder:text-text-tertiary'
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className='absolute right-2 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary'>
                <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                </svg>
              </button>
            )}
          </div>

          <div className='h-5 w-px bg-border-subtle' />

          {/* Add Customer */}
          <div className='relative' ref={addSearchRef}>
            <button
              onClick={() => setShowAddDropdown(!showAddDropdown)}
              className='flex items-center gap-1.5 px-3.5 py-2 text-body-xs font-semibold bg-button-primary text-white rounded-xl hover:bg-button-primary-hover transition-colors'>
              <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
              </svg>
              Add Customer
            </button>
            {showAddDropdown && (
              <div className='absolute z-50 mt-1 right-0 w-96 bg-white border border-gray-200 rounded-lg shadow-lg'>
                <div className='p-3 border-b'>
                  <input
                    type='text'
                    value={addSearchTerm}
                    onChange={(e) => setAddSearchTerm(e.target.value)}
                    placeholder='Search all websites...'
                    className='w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500'
                    autoFocus
                  />
                </div>
                <div className='max-h-64 overflow-y-auto'>
                  {addSearchTerm.length < 2 && (
                    <p className='text-sm text-gray-400 text-center py-4'>Type at least 2 characters to search</p>
                  )}
                  {addSearchResults.length === 0 && addSearchTerm.length >= 2 && (
                    <p className='text-sm text-gray-400 text-center py-4'>No matching websites found</p>
                  )}
                  {addSearchResults.map(({ website: w, listed, currentPlan, tierOptions }) => (
                    <div
                      key={w.website_id}
                      className='flex items-center justify-between px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0'>
                      <div className='min-w-0 flex-1'>
                        <p className='text-sm font-medium text-gray-900 truncate flex items-center gap-1.5'>
                          <span className='truncate'>{w.website_name || w.website_id}</span>
                          {listed ? (
                            <span className='shrink-0 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-accent-purple/10 text-accent-purple'>
                              In list
                            </span>
                          ) : null}
                        </p>
                        <p className='text-xs text-gray-400 font-mono truncate'>
                          {w.website_id}
                          {!listed ? (
                            <span className='ml-1.5 font-sans'>· currently {PLAN_DISPLAY[currentPlan] || currentPlan}</span>
                          ) : null}
                        </p>
                      </div>
                      <select
                        value={listed ? currentPlan : ""}
                        title={listed ? "Change this customer's tier" : "Add to Customer Analytics with this tier"}
                        onChange={(e) => {
                          const tier = e.target.value;
                          if (!tier) return;
                          if (listed) {
                            if (tier !== currentPlan) handlePlanChange(w.website_id, tier);
                          } else {
                            handleAddCustomer(w.website_id, tier);
                          }
                        }}
                        className='ml-2 px-2 py-1 text-xs border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500'>
                        {!listed ? (
                          <option value='' disabled>
                            Set tier...
                          </option>
                        ) : null}
                        {tierOptions.map((tier) => (
                          <option key={tier} value={tier}>
                            {PLAN_DISPLAY[tier] || tier}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
      </div>

      {/* Customer Table */}
      <div className='bg-white rounded-2xl border border-border-subtle overflow-hidden'>
        <div className='overflow-x-auto'>
          <table className='w-full'>
            <thead>
              <tr className='bg-surface-neutral border-b border-border-default'>
                <SortHeader
                  field='health'
                  title='Weighted 0–100: Hub recency 20% (days since last login/activity), Hub frequency 15% (active days in 30d), team adoption 10% (active users in 30d), findings acted on 25% (done or discarded in 30d), agents in use 10% (distinct agents that ran in 30d), delivery 10% (runs in 30d), Est. NPS 10%. Hover a score for the breakdown.'>
                  Health
                </SortHeader>
                <SortHeader field='name'>Customer</SortHeader>
                <SortHeader field='plan'>Plan</SortHeader>
                <SortHeader field='nps'>Est. NPS</SortHeader>
                <SortHeader field='agents' title='Distinct agents that ran on the production Hub in the last 30 days (not launches). Hover for the list.'>
                  Agents / Mo
                </SortHeader>
                <SortHeader field='findings' title='Findings the customer marked done or discarded in the last 30 days (Zenyt staff actions excluded). Hover for the breakdown.'>
                  Findings acted on
                </SortHeader>
                <SortHeader field='activity' title={activityHeaderTitle}>
                  Activity / wk
                </SortHeader>
                <SortHeader
                  field='lastActive'
                  title='Most recent agent run (scan start time) for this website.'>
                  Last agent launch
                </SortHeader>
                <th className='px-4 py-2.5 text-left text-caption font-semibold text-text-secondary uppercase tracking-[0.06em]'>
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-border-subtle'>
              {filteredCustomers.map((customer) => {
                const nps = customer.nps;
                const isExpanded = expandedCustomer === customer.prodId;
                return (
                  <React.Fragment key={customer.prodId}>
                    <tr className='hover:bg-surface-neutral/50 transition-colors'>
                      <td className='px-4 py-3 whitespace-nowrap'>
                        {(() => {
                          const detail = customer.health;
                          const score = detail?.score ?? 0;
                          let color;
                          let label;
                          if (score >= 70) {
                            color = "bg-accent-green/10 text-accent-green";
                            label = "Healthy";
                          } else if (score >= 40) {
                            color = "bg-severity-medium/10 text-severity-medium";
                            label = "At Risk";
                          } else {
                            color = "bg-severity-high/10 text-severity-high";
                            label = "Unhealthy";
                          }
                          if (!detail?.parts) {
                            return (
                              <div className='inline-flex items-center gap-2'>
                                <span className={`px-2 py-0.5 text-caption font-bold rounded-lg ${color}`}>{score}</span>
                                <span className={`text-caption font-medium ${color.split(" ")[1]}`}>{label}</span>
                              </div>
                            );
                          }
                          return (
                            <div
                              onMouseEnter={(e) => openHoverCard("health", customer.prodId, e.currentTarget)}
                              onMouseLeave={() => closeHoverCardIf("health", customer.prodId)}
                              className='inline-flex cursor-default items-center gap-2'>
                              <span className={`px-2 py-0.5 text-caption font-bold rounded-lg ${color}`}>{score}</span>
                              <span className={`text-caption font-medium ${color.split(" ")[1]}`}>{label}</span>
                            </div>
                          );
                        })()}
                      </td>
                      <td className='px-4 py-3 whitespace-nowrap'>
                        <div className='flex items-start gap-2'>
                          <button
                            type='button'
                            onClick={() => handleStarToggle(customer.prodId, !customer.starred)}
                            title={customer.starred ? "Starred — click to unstar" : "Click to star this customer"}
                            className='mt-0.5 shrink-0 text-text-tertiary hover:text-amber-500 transition-colors'>
                            <svg
                              className={`h-4 w-4 ${customer.starred ? "fill-amber-400 text-amber-500" : "fill-none"}`}
                              viewBox='0 0 24 24'
                              stroke='currentColor'
                              strokeWidth={2}>
                              <path strokeLinecap='round' strokeLinejoin='round' d='M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118L2.098 10.1c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.518-4.674z' />
                            </svg>
                          </button>
                          <div>
                            <div className='text-body-sm font-medium text-text-primary'>{customer.name}</div>
                            <div className='text-caption text-text-tertiary font-mono'>{customer.prodId}</div>
                          </div>
                        </div>
                      </td>
                      <td className='px-4 py-3 whitespace-nowrap'>
                        <PlanSelector
                          currentPlan={customer.plan}
                          onPlanChange={(newPlan) => handlePlanChange(customer.prodId, newPlan)}
                        />
                      </td>
                      <td className='px-4 py-3 whitespace-nowrap'>
                        <NpsEmojiPicker
                          currentScore={nps?.score}
                          onSelect={(score) => handleNpsChange(customer.prodId, score)}
                        />
                      </td>
                      <td className='px-4 py-3 whitespace-nowrap'>
                        <span
                          onMouseEnter={(e) => openHoverCard("agents", customer.prodId, e.currentTarget)}
                          onMouseLeave={() => closeHoverCardIf("agents", customer.prodId)}
                          className={`inline-block min-w-[24px] px-2 py-0.5 text-body-xs font-semibold rounded-md text-center cursor-default ${
                            customer.agents30d > 0 ? "bg-accent-green/10 text-accent-green" : "bg-surface-neutral-subtle text-text-tertiary"
                          }`}>
                          {customer.agents30d}
                        </span>
                        <span className='ml-1.5 text-caption text-text-tertiary'>{customer.runs30d} run{customer.runs30d === 1 ? "" : "s"}</span>
                      </td>
                      <td className='px-4 py-3 whitespace-nowrap'>
                        {customer.findings ? (
                          <span
                            onMouseEnter={(e) => openHoverCard("findings", customer.prodId, e.currentTarget)}
                            onMouseLeave={() => closeHoverCardIf("findings", customer.prodId)}
                            className={`inline-block min-w-[24px] px-2 py-0.5 text-body-xs font-semibold rounded-md text-center cursor-default ${
                              customer.findings.interacted > 0 ? "bg-accent-purple/10 text-accent-purple" : "bg-surface-neutral-subtle text-text-tertiary"
                            }`}>
                            {customer.findings.interacted}
                          </span>
                        ) : (
                          <span className='text-caption text-text-tertiary' title='Not reported by Platform on this environment'>—</span>
                        )}
                        {customer.findings ? (
                          <span className='ml-1.5 text-caption text-text-tertiary'>
                            {customer.findings.done} done · {customer.findings.discarded} disc.
                          </span>
                        ) : null}
                      </td>
                      <td className='px-4 py-3 whitespace-nowrap'>
                        {(() => {
                          if (overviewLoading) {
                            return <span className='text-caption text-text-tertiary'>…</span>;
                          }
                          const act = customer.biActivity;
                          if (!act) {
                            return <span className='text-caption text-text-tertiary'>—</span>;
                          }
                          const now = new Date();
                          const rel = (d) => {
                            const daysSince = Math.floor((now - d) / (1000 * 60 * 60 * 24));
                            if (daysSince === 0) return "Today";
                            if (daysSince === 1) return "Yesterday";
                            return `${daysSince}d ago`;
                          };
                          // Most recent signal wins: a valid token means no new login row,
                          // so "Login 20d ago" could hide Hub activity from today.
                          const recent = mostRecentConsoleSignal(act.last_login_at, act.last_activity_at);
                          const subLine = recent ? `${recent.label} ${rel(recent.ts)}` : null;
                          return (
                            <div>
                              <span
                                className='inline-block min-w-[28px] px-2 py-0.5 text-body-xs font-semibold rounded-md text-center bg-accent-purple/10 text-accent-purple'
                                title={`Rate over the last ${act.rate_days || 90} days · ${act.total_actions} events and ${act.login_count} logins in the last year`}>
                                {act.actions_per_week}/wk
                              </span>
                              {subLine ? (
                                <div className='text-caption text-text-tertiary mt-0.5'>{subLine}</div>
                              ) : (
                                <div className='text-caption text-text-tertiary mt-0.5'>—</div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className='px-4 py-3 whitespace-nowrap'>
                        {(() => {
                          const d = customer.lastActiveDate;
                          if (!d) return <span className='text-caption text-text-tertiary'>Never</span>;
                          const daysSince = calendarDaysSince(d);
                          let relColor = "text-accent-green";
                          if (daysSince > 14) relColor = "text-severity-high";
                          else if (daysSince > 7) relColor = "text-severity-medium";
                          return (
                            <div>
                              <span className={`text-body-xs font-medium ${relColor}`}>
                                {daysSince === 0 ? "Today" : daysSince === 1 ? "Yesterday" : `${daysSince}d ago`}
                              </span>
                              <div className='text-caption text-text-tertiary'>
                                {d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                      <td className='px-4 py-3 whitespace-nowrap'>
                        <button
                          onClick={() => setExpandedCustomer(isExpanded ? null : customer.prodId)}
                          className='text-accent-purple hover:opacity-80 text-body-xs font-medium flex items-center gap-1 transition-opacity'>
                          {isExpanded ? "Hide" : "View"}
                          <svg
                            className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                            fill='none'
                            viewBox='0 0 24 24'
                            stroke='currentColor'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                          </svg>
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={9} className='px-6 py-5 bg-accent-purple/[0.02]'>
                          <div className='flex flex-col xl:flex-row gap-6'>
                            <div className='xl:w-[380px] shrink-0 space-y-5'>
                              <div>
                                <h3 className='text-body-xs font-semibold text-text-secondary mb-2'>NPS History</h3>
                                <NpsHistoryChart websiteId={customer.prodId} />
                              </div>
                              <div>
                                <h3 className='text-body-xs font-semibold text-text-secondary mb-2'>Agents Launched Per Week</h3>
                                <AgentsPerWeekChart websiteId={customer.prodId} enabled={isExpanded} />
                              </div>
                              <div>
                                <h3 className='text-body-xs font-semibold text-text-secondary mb-1'>User activity (DAU / WAU)</h3>
                                <p className='text-caption text-text-tertiary mb-2'>Calendar days in US/Eastern; same attribution as the BI dashboard.</p>
                                <CustomerBrandActivityTrends
                                  brandName={customer.name}
                                  enabled={isExpanded}
                                  height={200}
                                  startDate={biActivityWindow.startDate}
                                  endDate={biActivityWindow.endDate}
                                />
                              </div>
                              <div>
                                <CustomerBrandActiveUsers
                                  brandName={customer.name}
                                  enabled={isExpanded}
                                  startDate={biActivityWindow.startDate}
                                  endDate={biActivityWindow.endDate}
                                />
                              </div>
                            </div>
                            <div className='flex-1 min-w-0'>
                              <CustomerSessions
                                brandName={customer.name}
                                enabled={isExpanded}
                                startDate={biActivityWindow.startDate}
                                endDate={biActivityWindow.endDate}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {typeof document !== "undefined" &&
        hoverCard &&
        hoverCardRect &&
        customerByProdId[hoverCard.prodId] &&
        createPortal(
          (() => {
            const c = customerByProdId[hoverCard.prodId];
            if (hoverCard.kind === "health") {
              if (!c.health?.parts?.length) return null;
              return <HealthBreakdownTooltipPanel detail={c.health} score={c.health.score} anchorRect={hoverCardRect} />;
            }
            const b = hoverCard.kind === "agents" ? agentsBreakdown(c, hubBase) : findingsBreakdown(c.findings);
            return (
              <HoverCardPanel
                title={hoverCard.kind === "agents" ? "Agents on their Hub · 30d" : "Findings acted on · 30d"}
                headline={b.headline}
                rows={b.rows}
                anchorRect={hoverCardRect}
              />
            );
          })(),
          document.body
        )}
    </div>
  );
}

export default CustomerAnalytics;
